import { useEffect, useState } from "react";
import { FiImage, FiPlus, FiEdit2, FiX } from "react-icons/fi";
import { TYPES } from "../constants";

const ContentForm = ({ activeType, onSubmit, submitting, setNotif, editingProduct, onCancelEdit }) => {
  const [form, setForm] = useState({
    name: "",
    price: "",
    shenime: "",
    type: activeType,
    isActive: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const isEditing = !!editingProduct;

  useEffect(() => {
    setForm((p) => ({ ...p, type: activeType }));
  }, [activeType]);

  useEffect(() => {
    if (editingProduct) {
      setForm({
        name: editingProduct.name || "",
        price: editingProduct.price != null ? String(editingProduct.price) : "",
        shenime: editingProduct.shenime || editingProduct.description || "",
        type: editingProduct.type || activeType,
        isActive: editingProduct.isActive !== false,
      });
      setImageFile(null);
      setImagePreview(editingProduct.imageUrl || "");
    } else {
      setForm({
        name: "",
        price: "",
        shenime: "",
        type: activeType,
        isActive: true,
      });
      setImageFile(null);
      setImagePreview("");
    }
  }, [editingProduct?.id]);

  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImagePreview(url);
      return () => URL.revokeObjectURL(url);
    }
    if (!isEditing) setImagePreview("");
    else if (editingProduct?.imageUrl) setImagePreview(editingProduct.imageUrl);
  }, [imageFile, isEditing, editingProduct?.imageUrl]);

  const resetForm = () => {
    setForm({
      name: "",
      price: "",
      shenime: "",
      type: activeType,
      isActive: true,
    });
    setImageFile(null);
    setImagePreview("");
    onCancelEdit?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotif?.({ message: "" });

    if (!isEditing && !imageFile) {
      setNotif?.({ type: "error", message: "Zgjedh një foto për produktin." });
      return;
    }

    if (!form.name.trim()) {
      setNotif?.({ type: "error", message: "Emri është i detyrueshëm." });
      return;
    }

    const priceNum = Number(form.price);
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      setNotif?.({
        type: "error",
        message: "Çmimi duhet të jetë numër valid.",
      });
      return;
    }

    if (isEditing) {
      const ok = await onSubmit({
        id: editingProduct.id,
        name: form.name.trim(),
        price: priceNum,
        shenime: form.shenime.trim(),
        type: form.type,
        isActive: form.isActive,
        imageFile: imageFile || null,
        existingImageUrl: editingProduct.imageUrl || "",
      });
      if (ok) resetForm();
    } else {
      const ok = await onSubmit({
        name: form.name.trim(),
        price: priceNum,
        shenime: form.shenime.trim(),
        type: form.type,
        isActive: form.isActive,
        imageFile,
      });
      if (ok) resetForm();
    }
  };

  return (
    <div className="lg:col-span-2 rounded-2xl border border-[#a6a6a6]/30 bg-white p-4 sm:p-5 shadow-sm w-full min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base sm:text-lg font-semibold text-[#000000]">
          {isEditing ? "Ndrysho produktin" : `Shto ${TYPES.find((x) => x.key === activeType)?.label}`}
        </h3>
        <div className="flex items-center gap-2">
          {isEditing && (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border border-[#a6a6a6]/50 px-3 py-2 text-xs text-[#000000] hover:bg-[#a6a6a6]/10 flex items-center gap-1.5"
            >
              <FiX size={14} /> Anulo
            </button>
          )}
          <span className="rounded-full bg-[#e0d8c3]/40 px-3 py-1 text-xs font-medium text-[#000000]">
            {activeType}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-[#000000]">
            Foto {isEditing && <span className="text-[#a6a6a6] font-normal">(lër bosh për të mbajtur të njëjtën)</span>}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-[#a6a6a6]/50 bg-white px-4 py-3 min-h-[44px] text-sm hover:bg-[#e0d8c3]/20 transition touch-manipulation">
            <FiImage className="text-[#000000] shrink-0" />
            {isEditing ? "Ndrysho foto" : "Zgjedh foto"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            />
          </label>

          {imagePreview && (
            <div className="mt-3 overflow-hidden rounded-xl border border-[#a6a6a6]/30">
              <img
                src={imagePreview}
                alt="Parapamje"
                className="h-40 sm:h-44 w-full object-cover"
              />
            </div>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[#000000]">
            Emri
          </label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            className="w-full rounded-xl border border-[#a6a6a6]/50 px-3 py-3 text-base sm:text-sm text-[#000000] focus:ring-2 focus:ring-[#e0d8c3] focus:border-[#e0d8c3] min-h-[44px] touch-manipulation bg-white"
            placeholder="p.sh. Dior Sauvage"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[#000000]">
            Shenime / Përshkrim
          </label>
          <textarea
            rows={3}
            value={form.shenime}
            onChange={(e) => setForm((p) => ({ ...p, shenime: e.target.value }))}
            className="w-full rounded-xl border border-[#a6a6a6]/50 px-3 py-3 text-base sm:text-sm text-[#000000] focus:ring-2 focus:ring-[#e0d8c3] focus:border-[#e0d8c3] touch-manipulation bg-white resize-none"
            placeholder="Përshkrim i shkurtër ose shenime për produktin (opsional)"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[#000000]">
            Çmimi (€)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            required
            value={form.price}
            onChange={(e) =>
              setForm((p) => ({ ...p, price: e.target.value }))
            }
            className="w-full rounded-xl border border-[#a6a6a6]/50 px-3 py-3 text-base sm:text-sm text-[#000000] focus:ring-2 focus:ring-[#e0d8c3] focus:border-[#e0d8c3] min-h-[44px] touch-manipulation bg-white"
            placeholder="p.sh. 20"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-[#000000]">
            Aktive
          </label>
          <select
            value={form.isActive ? "true" : "false"}
            onChange={(e) =>
              setForm((p) => ({ ...p, isActive: e.target.value === "true" }))
            }
            className="w-full rounded-xl border border-[#a6a6a6]/50 px-3 py-3 bg-white text-base sm:text-sm text-[#000000] focus:ring-2 focus:ring-[#e0d8c3] min-h-[44px] touch-manipulation"
          >
            <option value="true">Po</option>
            <option value="false">Jo</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#000000] py-3 font-medium text-[#ffffff] hover:bg-[#000000]/90 transition disabled:opacity-60 min-h-[48px] touch-manipulation"
        >
          {isEditing ? <FiEdit2 size={18} /> : <FiPlus size={18} />}
          {submitting ? "Duke ruajtur..." : isEditing ? "Ruaj ndryshimet" : "Shto produkt"}
        </button>
      </form>
    </div>
  );
};

export default ContentForm;
